/**
 * Client-side AI Service
 * Uses Google Gemini API directly from the browser
 * Fallback to rule-based SQL generation when API is unavailable
 */

const GEMINI_API_URL = 'https://generativelanguage.googleapis.com/v1/models/gemini-2.5-flash:generateContent';

class ClientAIService {
  constructor() {
    this.apiKey = localStorage.getItem('gemini_api_key') || null;
  }

  setApiKey(key) {
    this.apiKey = key;
    if (key) {
      localStorage.setItem('gemini_api_key', key);
    } else {
      localStorage.removeItem('gemini_api_key');
    }
  }

  getApiKey() {
    return this.apiKey || localStorage.getItem('gemini_api_key');
  }

  hasApiKey() {
    return !!this.getApiKey();
  }

  /**
   * Translate natural language to SQL using Gemini API
   */
  async translateToSQL(question, columns, tableName, sampleData = null) {
    const apiKey = this.getApiKey();

    if (!apiKey) {
      // Fall back to rule-based SQL generation
      return this.generateRuleBasedSQL(question, columns, tableName);
    }

    try {
      const prompt = this.buildSQLPrompt(question, columns, tableName, sampleData);

      const response = await fetch(`${GEMINI_API_URL}?key=${apiKey}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          contents: [{
            parts: [{ text: prompt }]
          }],
          generationConfig: {
            temperature: 0.1,
            maxOutputTokens: 500,
          }
        })
      });

      if (!response.ok) {
        const error = await response.json();
        if (response.status === 429) {
          throw new Error('API rate limit exceeded. Please wait a moment and try again.');
        }
        throw new Error(error.error?.message || 'Gemini API error');
      }

      const data = await response.json();
      const text = data.candidates?.[0]?.content?.parts?.[0]?.text || '';

      return this.extractSQL(text);
    } catch (error) {
      console.error('Gemini API error:', error);
      // Fall back to rule-based generation
      return this.generateRuleBasedSQL(question, columns, tableName);
    }
  }

  /**
   * Generate natural language answer from query results
   */
  async generateAnswer(question, sql, results, rowCount) {
    const apiKey = this.getApiKey();

    if (!apiKey) {
      return this.generateBasicAnswer(question, results, rowCount);
    }

    try {
      const prompt = this.buildAnswerPrompt(question, sql, results, rowCount);

      const response = await fetch(`${GEMINI_API_URL}?key=${apiKey}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          contents: [{
            parts: [{ text: prompt }]
          }],
          generationConfig: {
            temperature: 0.3,
            maxOutputTokens: 1000,
          }
        })
      });

      if (!response.ok) {
        return this.generateBasicAnswer(question, results, rowCount);
      }

      const data = await response.json();
      return data.candidates?.[0]?.content?.parts?.[0]?.text || this.generateBasicAnswer(question, results, rowCount);
    } catch (error) {
      console.error('Answer generation error:', error);
      return this.generateBasicAnswer(question, results, rowCount);
    }
  }

  /**
   * Generate data insights from dataset
   */
  async generateDataInsights(tableName, columns, sampleData, stats) {
    const apiKey = this.getApiKey();

    if (!apiKey) {
      return this.generateBasicInsights(columns, stats);
    }

    try {
      const prompt = `Analyze this dataset and provide business insights:

Table: ${tableName}
Columns: ${columns.join(', ')}
Sample Data: ${JSON.stringify(sampleData?.slice(0, 3), null, 2)}
Statistics: ${JSON.stringify(stats, null, 2)}

Provide:
1. Key observations about the data
2. Potential business questions this data can answer
3. Data quality observations
4. Suggested analyses

Be concise and actionable.`;

      const response = await fetch(`${GEMINI_API_URL}?key=${apiKey}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          contents: [{ parts: [{ text: prompt }] }],
          generationConfig: { temperature: 0.4, maxOutputTokens: 1000 }
        })
      });

      if (!response.ok) {
        return this.generateBasicInsights(columns, stats);
      }

      const data = await response.json();
      return data.candidates?.[0]?.content?.parts?.[0]?.text || this.generateBasicInsights(columns, stats);
    } catch (error) {
      return this.generateBasicInsights(columns, stats);
    }
  }

  // Helper methods
  buildSQLPrompt(question, columns, tableName, sampleData) {
    return `You are a SQL expert. Generate a DuckDB SQL query for this question.

Table: ${tableName}
Columns: ${columns.join(', ')}
${sampleData ? `Sample data: ${JSON.stringify(sampleData.slice(0, 2))}` : ''}

Question: ${question}

Rules:
- Return ONLY the SQL query, no explanation
- Use exact column names (case-sensitive)
- For text searches, use ILIKE for case-insensitive matching
- Always include proper WHERE clauses for filters
- Use aggregate functions (COUNT, SUM, AVG) when asking about totals or averages
- Limit results to 100 unless specified otherwise

SQL:`;
  }

  buildAnswerPrompt(question, sql, results, rowCount) {
    const resultSample = results?.slice(0, 5) || [];
    return `Answer this question based on the query results:

Question: ${question}
SQL Query: ${sql}
Total Results: ${rowCount}
Sample Results: ${JSON.stringify(resultSample, null, 2)}

Provide a clear, concise answer that:
1. Directly answers the question
2. Highlights key findings
3. Mentions any notable patterns or outliers
4. Is written for business users (not technical)

Answer:`;
  }

  extractSQL(text) {
    // Extract SQL from potential code blocks
    const codeBlockMatch = text.match(/```(?:sql)?\s*([\s\S]*?)```/);
    if (codeBlockMatch) {
      return codeBlockMatch[1].trim();
    }

    // Clean up the response
    let sql = text.trim();
    sql = sql.replace(/^SQL:\s*/i, '');
    sql = sql.split('\n').filter(line => !line.startsWith('--') && line.trim()).join('\n');

    return sql;
  }

  /**
   * Rule-based SQL generation (fallback)
   */
  generateRuleBasedSQL(question, columns, tableName) {
    const q = question.toLowerCase();
    const extractLimit = (text) => {
      const limitMatch = text.match(/(?:first|top|bottom|last|limit)\s+(\d+)/i);
      if (limitMatch) return parseInt(limitMatch[1], 10);
      const rowsMatch = text.match(/(\d+)\s+(?:rows|records|entries)/i);
      if (rowsMatch) return parseInt(rowsMatch[1], 10);
      return null;
    };
    const explicitLimit = extractLimit(q);

    // Pattern matching for common queries
    if (q.includes('show') && (q.includes('all') || q.includes('everything'))) {
      const limit = explicitLimit || 100;
      return `SELECT * FROM ${tableName} LIMIT ${limit};`;
    }

    if (q.includes('count') || q.includes('how many')) {
      // Find potential grouping column
      const groupCol = this.findMentionedColumn(q, columns) || columns[0];
      if (q.includes('total') || q.includes('all')) {
        return `SELECT COUNT(*) as total_count FROM ${tableName};`;
      }
      const limit = explicitLimit || 20;
      return `SELECT "${groupCol}", COUNT(*) as count FROM ${tableName} GROUP BY "${groupCol}" ORDER BY count DESC LIMIT ${limit};`;
    }

    if (q.includes('average') || q.includes('avg') || q.includes('mean')) {
      const numCol = this.findNumericColumn(columns, q);
      if (numCol) {
        const groupCol = this.findMentionedColumn(q, columns.filter(c => c !== numCol));
        if (groupCol) {
          const limit = explicitLimit || 20;
          return `SELECT "${groupCol}", AVG("${numCol}") as average FROM ${tableName} GROUP BY "${groupCol}" ORDER BY average DESC LIMIT ${limit};`;
        }
        return `SELECT AVG("${numCol}") as average FROM ${tableName};`;
      }
    }

    if (q.includes('sum') || q.includes('total')) {
      const numCol = this.findNumericColumn(columns, q);
      if (numCol) {
        const groupCol = this.findMentionedColumn(q, columns.filter(c => c !== numCol));
        if (groupCol) {
          const limit = explicitLimit || 20;
          return `SELECT "${groupCol}", SUM("${numCol}") as total FROM ${tableName} GROUP BY "${groupCol}" ORDER BY total DESC LIMIT ${limit};`;
        }
        return `SELECT SUM("${numCol}") as total FROM ${tableName};`;
      }
    }

    if (q.includes('unique') || q.includes('distinct')) {
      const col = this.findMentionedColumn(q, columns) || columns[0];
      const limit = explicitLimit || 50;
      return `SELECT DISTINCT "${col}" FROM ${tableName} ORDER BY "${col}" LIMIT ${limit};`;
    }

    if (q.includes('top') || q.includes('highest') || q.includes('most')) {
      const numCol = this.findNumericColumn(columns, q) || columns.find(c =>
        c.toLowerCase().includes('price') || c.toLowerCase().includes('amount') || c.toLowerCase().includes('value')
      );
      const limitMatch = q.match(/top\s*(\d+)/i);
      const limit = limitMatch ? parseInt(limitMatch[1]) : 10;
      if (numCol) {
        return `SELECT * FROM ${tableName} ORDER BY "${numCol}" DESC LIMIT ${limit};`;
      }
    }

    if (q.includes('bottom') || q.includes('lowest') || q.includes('least')) {
      const numCol = this.findNumericColumn(columns, q);
      const limitMatch = q.match(/bottom\s*(\d+)/i);
      const limit = limitMatch ? parseInt(limitMatch[1]) : 10;
      if (numCol) {
        return `SELECT * FROM ${tableName} ORDER BY "${numCol}" ASC LIMIT ${limit};`;
      }
    }

    if (q.includes('where') || q.includes('filter') || q.includes('with')) {
      // Try to extract filter conditions
      const col = this.findMentionedColumn(q, columns);
      const valueMatch = q.match(/['"]([^'"]+)['"]|=\s*(\w+)|is\s+(\w+)/i);
      if (col && valueMatch) {
        const value = valueMatch[1] || valueMatch[2] || valueMatch[3];
        const limit = explicitLimit || 100;
        return `SELECT * FROM ${tableName} WHERE "${col}" ILIKE '%${value}%' LIMIT ${limit};`;
      }
    }

    // Look for column mention and show those results
    const mentionedCol = this.findMentionedColumn(q, columns);
    if (mentionedCol) {
      const limit = explicitLimit || 20;
      return `SELECT "${mentionedCol}", COUNT(*) as count FROM ${tableName} GROUP BY "${mentionedCol}" ORDER BY count DESC LIMIT ${limit};`;
    }

    // Default: show first rows
    const limit = explicitLimit || 10;
    return `SELECT * FROM ${tableName} LIMIT ${limit};`;
  }

  findMentionedColumn(question, columns) {
    const q = question.toLowerCase();
    for (const col of columns) {
      if (q.includes(col.toLowerCase())) {
        return col;
      }
      // Check for partial matches
      const colWords = col.toLowerCase().split(/[_\s-]+/);
      for (const word of colWords) {
        if (word.length > 3 && q.includes(word)) {
          return col;
        }
      }
    }
    return null;
  }

  findNumericColumn(columns, question) {
    // Find likely numeric columns based on keywords
    const numericKeywords = ['price', 'amount', 'total', 'count', 'quantity', 'value', 'cost', 'revenue', 'sales', 'profit', 'bid', 'fee', 'rate', 'percent', 'number', 'num', 'id'];

    // First, try to find a mentioned column that looks numeric
    const mentioned = this.findMentionedColumn(question, columns);
    if (mentioned) {
      const lower = mentioned.toLowerCase();
      const looksNumeric = numericKeywords.some(kw => lower.includes(kw));
      if (looksNumeric) return mentioned;
    }

    // Otherwise, find any likely numeric column
    return columns.find(col => {
      const lower = col.toLowerCase();
      return numericKeywords.some(kw => lower.includes(kw));
    });
  }

  generateBasicAnswer(question, results, rowCount) {
    if (!results || results.length === 0) {
      return `Your query returned no results. Try adjusting your search criteria or check if the data matches your filter conditions.`;
    }

    const columns = Object.keys(results[0]);
    const q = question.toLowerCase();

    // Single aggregate result (e.g., COUNT(*), SUM, AVG)
    if (rowCount === 1 && columns.length <= 3) {
      const parts = [];
      for (const col of columns) {
        const value = results[0][col];
        const formattedValue = this.formatValue(value, col);

        if (col.toLowerCase().includes('count') || col === 'total_count') {
          parts.push(`There are **${formattedValue}** records`);
        } else if (col.toLowerCase().includes('avg') || col.toLowerCase().includes('average')) {
          parts.push(`The average is **${formattedValue}**`);
        } else if (col.toLowerCase().includes('sum') || col.toLowerCase().includes('total')) {
          parts.push(`The total is **${formattedValue}**`);
        } else if (col.toLowerCase().includes('min')) {
          parts.push(`The minimum is **${formattedValue}**`);
        } else if (col.toLowerCase().includes('max')) {
          parts.push(`The maximum is **${formattedValue}**`);
        } else {
          parts.push(`**${col}**: ${formattedValue}`);
        }
      }
      return parts.join('. ') + '.';
    }

    // Grouped/aggregated results with rankings
    if (columns.length === 2 && rowCount > 1) {
      const [labelCol, valueCol] = columns;
      const isCount = valueCol.toLowerCase().includes('count');
      const isAvg = valueCol.toLowerCase().includes('avg') || valueCol.toLowerCase().includes('average');

      const top3 = results.slice(0, 3);
      const topItem = results[0];
      const topValue = this.formatValue(topItem[valueCol], valueCol);

      let noun = isCount ? 'records' : (isAvg ? '' : '');

      let answer = `**${topItem[labelCol]}** is the top result with ${topValue}${noun ? ' ' + noun : ''}`;

      if (rowCount > 1) {
        const rankings = top3.map((row, i) =>
          `${i + 1}. **${row[labelCol]}** (${this.formatValue(row[valueCol], valueCol)})`
        ).join('\n');

        answer = `Here are the top ${Math.min(rowCount, 3)} results:\n\n${rankings}`;

        if (rowCount > 3) {
          answer += `\n\n...and ${rowCount - 3} more. See the full results below.`;
        }
      }

      return answer;
    }

    // Multiple columns - provide summary
    if (rowCount > 0) {
      const firstRow = results[0];

      // Check if this looks like a "top N" or "show me" query
      if (q.includes('top') || q.includes('highest') || q.includes('best') || q.includes('most')) {
        const numericCols = columns.filter(col => typeof firstRow[col] === 'number');
        const primaryCol = columns[0];
        const valueCol = numericCols[0] || columns[1];

        if (valueCol) {
          const top3 = results.slice(0, 3).map((row, i) =>
            `${i + 1}. **${row[primaryCol]}** (${this.formatValue(row[valueCol], valueCol)})`
          ).join('\n');

          return `Here are the top results:\n\n${top3}${rowCount > 3 ? `\n\n...and ${rowCount - 3} more in the table below.` : ''}`;
        }
      }

      // Default listing
      if (rowCount <= 5) {
        const listing = results.map((row, i) => {
          const mainValue = row[columns[0]];
          const secondaryValues = columns.slice(1, 3).map(c => `${c}: ${this.formatValue(row[c], c)}`).join(', ');
          return `• **${mainValue}**${secondaryValues ? ` (${secondaryValues})` : ''}`;
        }).join('\n');

        return `Found ${rowCount} result${rowCount > 1 ? 's' : ''}:\n\n${listing}`;
      }

      // Larger result set
      return `Found **${rowCount.toLocaleString()}** results matching your query. The data includes ${columns.length} columns: ${columns.slice(0, 4).join(', ')}${columns.length > 4 ? '...' : ''}. See the detailed results table below.`;
    }

    return `Found ${rowCount.toLocaleString()} results. Review the table below for details.`;
  }

  formatValue(value, columnName) {
    if (value === null || value === undefined) return 'N/A';

    const col = columnName.toLowerCase();

    // Format numbers
    if (typeof value === 'number') {
      // Currency-like columns
      if (col.includes('price') || col.includes('amount') || col.includes('cost') ||
        col.includes('revenue') || col.includes('total') || col.includes('value') ||
        col.includes('salary') || col.includes('budget')) {
        return new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD',
          minimumFractionDigits: 0,
          maximumFractionDigits: 0
        }).format(value);
      }

      // Percentages
      if (col.includes('percent') || col.includes('rate') || col.includes('ratio')) {
        return `${value.toFixed(1)}%`;
      }

      // Large numbers
      if (value >= 1000000) {
        return `${(value / 1000000).toFixed(1)}M`;
      }
      if (value >= 1000) {
        return value.toLocaleString();
      }

      // Decimals
      if (!Number.isInteger(value)) {
        return value.toFixed(2);
      }

      return value.toLocaleString();
    }

    return String(value);
  }

  generateBasicInsights(columns, stats) {
    const insights = [];

    insights.push(`**Dataset Overview**`);
    insights.push(`- ${columns.length} columns available for analysis`);
    insights.push(`- Columns: ${columns.slice(0, 5).join(', ')}${columns.length > 5 ? '...' : ''}`);

    if (stats) {
      insights.push(`\n**Quick Statistics**`);
      if (stats.totalRows) insights.push(`- Total records: ${stats.totalRows.toLocaleString()}`);
      if (stats.nullCounts) {
        const nullCols = Object.entries(stats.nullCounts).filter(([_, v]) => v > 0);
        if (nullCols.length > 0) {
          insights.push(`- Columns with missing data: ${nullCols.map(([k, v]) => `${k} (${v})`).join(', ')}`);
        }
      }
    }

    insights.push(`\n**Suggested Questions**`);
    insights.push(`- "Show me the first 10 rows"`);
    insights.push(`- "How many records are there?"`);
    insights.push(`- "Count records by ${columns[0]}"`);

    return insights.join('\n');
  }
}

const clientAIService = new ClientAIService();
export default clientAIService;
